import { z } from 'zod';

import { defineService } from '@@api/lib/defineService';
import { KeywordsAndSeoTeaser } from '@@api/services/aiTools/schemas';
import {
    GeneeaEntity,
    Kickword,
    LinkGeneeaEntityTag,
    Metadata,
    NewKickword,
    NewTag,
    PrintMetadata,
    Tag,
    TargetTextLengthOptions,
    UnityKickword,
    UnityMetadata,
    UnitySeoCheck,
} from '@@api/services/metadata/schemas';
import {
    deserializeKickword,
    deserializeMetadata,
    deserializeSeoCheck,
    deserializeTag,
    serializeKickword,
    serializeMetadata,
    serializeTag,
} from '@@api/services/metadata/transformers';
import {
    PaginationParams,
    SearchParams,
    SemanticSearchParams,
} from '@@api/utils/schemas/queryParams';
import { Ids } from '@@api/utils/schemas/schemas';
import { paginated } from '@@api/utils/schemas/utils';
import config from '@@config';

import { UnityArticle } from '../content/schemas/article';

export const metadataService = defineService(
    (defineEndpoint) => ({
        metadata: {
            get: defineEndpoint({
                method: 'GET',
                path: '/metadata/:id',
                omitContentLanguageFromKey: true,
                pathParams: z.object({
                    id: z.number(),
                }),
                query: z.object({
                    includePreviewUrl: z.literal(true).optional(),
                }),
                responses: {
                    200: UnityMetadata.transform(deserializeMetadata),
                },
            }),
            getAll: defineEndpoint({
                method: 'GET',
                path: '/metadata',
                query: z.object({
                    ...SearchParams.shape,
                    ...PaginationParams.shape,
                    ...SemanticSearchParams.shape,
                    useSearch: z.literal(true).optional(),
                    publishedOrReady: z.boolean().optional(),
                    excludeIds: Ids.optional(),
                    publishedBefore: z.string().nullish(),
                    publishedAfter: z.string().nullish(),
                }),
                responses: {
                    200: paginated(UnityMetadata.transform(deserializeMetadata)),
                },
            }),
            getMetadataId: defineEndpoint({
                method: 'GET',
                path: '/metadata',
                query: z.object({
                    resourceId: z.string().optional(),
                }),
                responses: {
                    200: z.object({ metadataId: z.number() }),
                },
            }),
            patch: defineEndpoint({
                method: 'PATCH',
                path: '/metadata/:id',
                pathParams: z.object({
                    id: z.number(),
                }),
                body: UnityMetadata.pick({
                    publicationStatus: true,
                    contentStatus: true,
                    workflowChecklist: true,
                }).partial(),
                responses: {
                    200: UnityMetadata.transform(deserializeMetadata),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/metadata/:id',
                pathParams: z.object({
                    id: z.number(),
                }),
                body: Metadata.transform(serializeMetadata),
                responses: {
                    200: UnityMetadata.transform(deserializeMetadata),
                },
            }),
            getList: defineEndpoint({
                method: 'GET',
                path: '/metadata/in',
                query: z.object({
                    ids: Ids,
                    size: z.number(),
                }),
                responses: {
                    200: paginated(UnityMetadata.transform(deserializeMetadata)),
                },
            }),
        },
        tags: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/tags',
                query: z.object({
                    ...SearchParams.pick({ q: true }).shape,
                    ...PaginationParams.shape,
                    active: z.boolean().optional(),
                    includeIds: Ids.optional(),
                    hasGeneeaEntity: z.boolean().optional(),
                }),
                responses: {
                    200: paginated(Tag.transform(deserializeTag)),
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/tags/:id',
                omitContentLanguageFromKey: true,
                pathParams: z.object({
                    id: z.number(),
                }),
                responses: {
                    200: Tag.transform(deserializeTag),
                },
            }),
            head: defineEndpoint({
                method: 'HEAD',
                path: '/tags',
                query: z.object({
                    name: z.string(),
                    excludeId: z.number(),
                }),
                responses: {
                    200: z.never(),
                    404: z.never(),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/tags/:id',
                pathParams: z.object({
                    id: z.number(),
                }),
                body: Tag.transform(serializeTag),
                responses: {
                    200: Tag.transform(deserializeTag),
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/tags',
                body: NewTag.transform(serializeTag),
                responses: {
                    201: Tag.transform(deserializeTag),
                },
            }),
            delete: defineEndpoint({
                method: 'DELETE',
                path: '/tags/:id',
                pathParams: z.object({
                    id: z.number(),
                }),
                responses: {
                    204: z.never(),
                },
            }),
        },
        geneeaEntities: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/geneea-entities',
                query: z.object({
                    ...SearchParams.pick({ q: true }).shape,
                    ...PaginationParams.shape,
                    hasTag: z.boolean().optional(),
                }),
                responses: {
                    200: paginated(GeneeaEntity),
                },
            }),
            linkTag: defineEndpoint({
                method: 'PUT',
                path: '/geneea-entities/:id/tag',
                pathParams: z.object({
                    id: z.number(),
                }),
                body: LinkGeneeaEntityTag,
                responses: {
                    200: GeneeaEntity,
                },
            }),
        },
        kickwords: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/kickwords',
                query: z.object({
                    ...SearchParams.pick({ q: true }).shape,
                    ...PaginationParams.shape,
                }),
                responses: {
                    200: paginated(UnityKickword.transform(deserializeKickword)),
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/kickwords/:id',
                omitContentLanguageFromKey: true,
                pathParams: z.object({
                    id: z.number(),
                }),
                responses: {
                    200: UnityKickword.transform(deserializeKickword),
                },
            }),
            head: defineEndpoint({
                method: 'HEAD',
                path: '/kickwords',
                query: z.object({
                    name: z.string(),
                    excludeId: z.number(),
                }),
                responses: {
                    200: z.never(),
                    404: z.never(),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/kickwords/:id',
                pathParams: z.object({
                    id: z.number(),
                }),
                body: Kickword.transform(serializeKickword),
                responses: {
                    200: UnityKickword.transform(deserializeKickword),
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/kickwords',
                body: NewKickword.transform(serializeKickword),
                responses: {
                    201: UnityKickword.transform(deserializeKickword),
                },
            }),
            delete: defineEndpoint({
                method: 'DELETE',
                path: '/kickwords/:id',
                pathParams: z.object({
                    id: z.number(),
                }),
                responses: {
                    204: z.never(),
                },
            }),
        },
        printMetadata: {
            patch: defineEndpoint({
                method: 'PATCH',
                path: '/metadata/:id/printMetadata',
                pathParams: z.object({
                    id: z.number(),
                }),
                body: PrintMetadata.partial(),
                responses: {
                    200: PrintMetadata,
                },
            }),
        },
        targetTextLengthOptions: {
            get: defineEndpoint({
                method: 'GET',
                path: '/target-text-length-options',
                responses: {
                    200: TargetTextLengthOptions,
                },
            }),
        },
        slugify: defineEndpoint({
            method: 'GET',
            path: '/slugify',
            query: z.object({
                text: z.string().default(''),
            }),
            responses: {
                200: z.string(),
            },
        }),
        seoChecker: {
            get: defineEndpoint({
                method: 'GET',
                path: '/seo-checker/articles/:id/seo-check',
                pathParams: z.object({
                    id: z.string(),
                }),
                responses: {
                    200: UnitySeoCheck.transform(deserializeSeoCheck),
                    404: z.object({ message: z.string() }),
                },
            }),
        },
        keywordsAndSeoTeaser: {
            post: defineEndpoint({
                method: 'POST',
                path: '/seo-teaser-generation-with-keywords/article/:id',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                body: z.object({
                    articleBody: z.string(),
                    numOfVariations: z.number(),
                }),
                responses: {
                    200: KeywordsAndSeoTeaser,
                },
            }),
        },
        userNeeds: {
            post: defineEndpoint({
                method: 'POST',
                path: '/metadata/:id/user-needs?replace=true',
                pathParams: z.object({ id: z.number() }),
                body: z.object({}),
                responses: {
                    202: z.array(z.never()),
                },
            }),
        },
    }),
    {
        baseUrl: config.apiEnvVars.metadataUrl,
    },
);
